import apiClient, { API_BASE_URL } from './api';
import axios from 'axios';
import { clearSession, refreshCurrentUser, storeSession } from './authSync';

// Authentication service to replace Firebase auth
class AuthService {
  // Register new user
  static async register(userData) {
    try {
      const response = await apiClient.post('/auth/register', {
        email: userData.email,
        username: userData.userName,
        display_name: userData.userName,
        password: userData.password,
      });

      // OTP flow: backend returns { detail, email }
      if (response.data && response.data.email) {
        return {
          success: true,
          pendingVerification: true,
          email: response.data.email,
          detail: response.data.detail,
          // R1C: false when the OTP email could not be sent (detail says what to do).
          emailSent: response.data.email_sent !== false,
        };
      }

      return { success: false, error: 'Registration failed: Invalid response' };
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Registration failed' 
      };
    }
  }

  static async verifyOtp({ email, otp }) {
    try {
      const response = await apiClient.post('/auth/verify-otp', { email, otp });
      if (response.data?.access_token && response.data?.user) {
        storeSession(response.data.access_token, response.data.user);
        return { success: true, user: response.data.user };
      }
      return { success: false, error: 'Verification failed: Invalid response' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'OTP verification failed' };
    }
  }

  static async resendOtp(email) {
    try {
      const response = await apiClient.post('/auth/resend-otp', { email });
      return { success: true, detail: response.data?.detail || 'OTP sent' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to resend OTP' };
    }
  }

  static async forgotPasswordRequest(email) {
    try {
      const response = await apiClient.post('/auth/forgot-password/request', { email });
      return { success: true, detail: response.data?.detail || 'If the email exists, a reset code was sent.' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to request password reset' };
    }
  }

  static async forgotPasswordReset({ email, otp, new_password }) {
    try {
      const response = await apiClient.post('/auth/forgot-password/reset', { email, otp, new_password });
      return { success: true, detail: response.data?.detail || 'Password updated' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to reset password' };
    }
  }

  // R1B.4: one-time invite link for admin-added vendors.
  static async checkSetPasswordLink(token) {
    try {
      const response = await apiClient.get('/auth/set-password/check', { params: { token } });
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, valid: false, detail: error.response?.data?.detail || 'Could not check this link' };
    }
  }

  static async setPasswordFromInvite({ token, password }) {
    try {
      const response = await apiClient.post('/auth/set-password', { token, password });
      if (response.data?.access_token && response.data?.user) {
        storeSession(response.data.access_token, response.data.user);
        const user = (await refreshCurrentUser()) || response.data.user;
        return { success: true, user };
      }
      return { success: false, error: 'Could not set your password' };
    } catch (error) {
      const detail = error.response?.data?.detail;
      return { success: false, error: typeof detail === 'string' ? detail : 'Could not set your password' };
    }
  }

  static async socialGoogle(credential) {
    try {
      const response = await apiClient.post('/auth/social/google', { credential });
      if (response.data?.access_token && response.data?.user) {
        storeSession(response.data.access_token, response.data.user);
        return { success: true, user: response.data.user };
      }
      return { success: false, error: 'Google login failed: Invalid response' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Google login failed' };
    }
  }

  static async socialFacebook(access_token) {
    try {
      const response = await apiClient.post('/auth/social/facebook', { access_token });
      if (response.data?.access_token && response.data?.user) {
        storeSession(response.data.access_token, response.data.user);
        return { success: true, user: response.data.user };
      }
      return { success: false, error: 'Facebook login failed: Invalid response' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Facebook login failed' };
    }
  }

  // Login user
  static async login(credentials) {
    try {
      // FastAPI expects form-urlencoded data for OAuth2 login
      // Use URLSearchParams so axios serializes correctly as application/x-www-form-urlencoded
      const params = new URLSearchParams();
      params.append('username', credentials.email); // Using email as username
      params.append('password', credentials.password);

      // Create a temporary axios instance for form-urlencoded request
      const loginClient = axios.create({
        baseURL: API_BASE_URL,
        withCredentials: true, // keep the refresh cookie when the API is on another origin
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const response = await loginClient.post('/auth/login', params);

      if (response.data && response.data.access_token) {
        if (response.data?.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
    }
        
        // Get user profile after login to get full user data including is_superuser
        const userProfile = await this.getCurrentUser();
        if (userProfile.success && userProfile.user) {
          storeSession(response.data.access_token, userProfile.user);
          return { success: true, user: userProfile.user };
        } else {
          // If getCurrentUser fails, still return success but log warning
          console.warn('Login successful but failed to fetch user profile:', userProfile.error);
          return { 
            success: false, 
            error: userProfile.error || 'Failed to fetch user profile after login' 
          };
        }
      }

      return { success: false, error: 'Login failed: No access token received' };
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle different types of errors
      let errorMessage = 'Login failed';
      
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        errorMessage = 'Network error: Backend server is not running. Please start the backend server on port 8000.';
      } else if (error.code === 'ECONNREFUSED' || error.message?.includes('ECONNREFUSED')) {
        errorMessage = 'Connection refused: Cannot connect to backend server. Please check if the backend is running and reachable.';
      } else if (error.response) {
        // Server responded with error
        errorMessage = error.response.data?.detail || error.response.statusText || 'Login failed';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  }

  // Get current user profile
  static async getCurrentUser() {
    try {
      const response = await apiClient.get('/users/me');
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Get user error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to get user data' 
      };
    }
  }

  // Logout user: best-effort server-side revoke, then clear local state
  // exactly as before regardless of whether that call succeeds.
  static async logout(redirectTo = '/') {
    try {
      await apiClient.post('/auth/logout', null, {
        headers: { 'X-Requested-With': 'zaynic' },
      });
    } catch (error) {
      console.error('Logout request failed (clearing local session anyway):', error);
    }
    clearSession(); // every open tab signs out too
    localStorage.removeItem('cart_items');
    localStorage.removeItem('wishlist_items');
    window.location.href = redirectTo || '/';
  }

  // Check if user is authenticated
  static isAuthenticated() {
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user_data');
    return !!(token && userData);
  }

  // Get stored user data
  static getStoredUser() {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  }

  // Update user profile
  static async updateProfile(profileData) {
    try {
      const response = await apiClient.put('/users/me', profileData);
      localStorage.setItem('user_data', JSON.stringify(response.data));
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Update profile error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to update profile' 
      };
    }
  }
}

export default AuthService;